import { invalidateLazy, retryCall } from '../guest/treeRefresh';
import { buildPgTableDdl } from '../guest/pgDdl';
import { connectExpandIds } from '../guest/connectExpand';
import { parseConnectionUrl } from '../guest/parseConnectionUrl';
import { copyTargetConnectionModal, copyTargetTableModal } from '../guest/copyDataPlan';
import { destructiveTableModal, destructiveTableSql, destructiveTableQueries, destructiveTableContext } from '../guest/destructiveTablePlan';

/** Guest-realm state, engines and the saved-connections store. Part of the DB Manager guest source (../entry.js joins the parts). */
export const guestCoreSource = `
(function () {
  var sdk = window.apibooSDK;
  var SECTION_ID = 'db-manager.sidebar';
  // Tested helpers from ./guest/treeRefresh.js (self-contained ES5, see there).
  var invalidateLazy = ${invalidateLazy.toString()};
  var retryCall = ${retryCall.toString()};
  var buildPgTableDdl = ${buildPgTableDdl.toString()};
  var destructiveTableModal = ${destructiveTableModal.toString()};
  var destructiveTableSql = ${destructiveTableSql.toString()};
  var destructiveTableQueries = ${destructiveTableQueries.toString()};
  var destructiveTableContext = ${destructiveTableContext.toString()};
  var connectExpandIds = ${connectExpandIds.toString()};
  var parseConnectionUrl = ${parseConnectionUrl.toString()};
  var copyTargetConnectionModal = ${copyTargetConnectionModal.toString()};
  var copyTargetTableModal = ${copyTargetTableModal.toString()};
  var BT = String.fromCharCode(96);
  // Node-id namespace separator. Generated connIds are [a-z0-9] only (and the
  // migrated legacy id is 'default'), so splitting a node id at the FIRST
  // separator always yields the owning connId no matter what identifiers
  // (which may themselves contain '|') follow it.
  var SEP = '|';

  // True when the vault is locked, so the tree can say "unlock to see your
  // connections" rather than showing an empty list, which would read as
  // "your connections are gone".
  var vaultLocked = false;

  // Saved descriptors (vault items), in display order.
  var connections = [];
  // Runtime state per connection id:
  //   { saved, password, createIfMissing, connected, connecting, error, status, treeNode }
  var conns = {};

  // Remembered so the engine picker defaults to the last-used engine.
  var lastEngine = 'mysql';

  // ---- Engine specs ---------------------------------------------------------
  // Everything engine-specific lives here: placeholder style, identifier
  // quoting, tree topology labels, schema queries, and column type options
  // for the new-table modal. $n placeholders are Postgres-native; MySQL and
  // SQLite use ?.

  function q(name) {
    // double-quote ident (postgres/sqlite)
    return '"' + String(name).replace(/"/g, '""') + '"';
  }
  function qbt(name) {
    // backtick ident (mysql)
    var re = new RegExp(BT, 'g');
    return BT + String(name).replace(re, BT + BT) + BT;
  }

  var ENGINES = {
    mysql: {
      label: 'MySQL',
      defaultPort: '3306',
      defaultUser: 'root',
      topLabel: 'Databases',
      rootIcon: 'db-mysql',
      quote: qbt,
      ph: function (n) { return '?'; },
      typeOptions: ['INT', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'DATETIME', 'DATE', 'BOOLEAN', 'DECIMAL(10,2)', 'FLOAT', 'JSON'],
      columnFeatures: ['nullable', 'primaryKey', 'unique', 'autoIncrement', 'default'],
      categories: ['columns', 'constraints', 'fks', 'refs', 'triggers', 'indexes', 'partitions'],
    },
    postgres: {
      label: 'PostgreSQL',
      defaultPort: '5432',
      defaultUser: 'postgres',
      topLabel: 'Schemas',
      rootIcon: 'db-postgres',
      quote: q,
      ph: function (n) { return '$' + n; },
      typeOptions: ['SERIAL', 'INTEGER', 'BIGINT', 'VARCHAR(255)', 'TEXT', 'TIMESTAMP', 'TIMESTAMPTZ', 'DATE', 'BOOLEAN', 'NUMERIC(10,2)', 'DOUBLE PRECISION', 'JSONB'],
      // No autoIncrement: Postgres does that through the SERIAL/BIGSERIAL types.
      columnFeatures: ['nullable', 'primaryKey', 'unique', 'default'],
      categories: ['columns', 'constraints', 'fks', 'refs', 'triggers', 'indexes'],
    },
    sqlite: {
      label: 'SQLite',
      topLabel: null, // tables hang directly off the connection root
      rootIcon: 'db-sqlite',
      quote: q,
      ph: function (n) { return '?'; },
      typeOptions: ['INTEGER', 'TEXT', 'REAL', 'NUMERIC', 'BLOB'],
      columnFeatures: ['nullable', 'primaryKey', 'unique', 'autoIncrement', 'default'],
      categories: ['columns', 'fks', 'triggers', 'indexes'],
    },
  };

  function spec(engine) { return ENGINES[engine] || ENGINES.mysql; }
  function quoteIdent(engine, name) { return spec(engine).quote(name); }
  function qualifiedIdent(engine, db, name) {
    return quoteIdent(engine, db) + '.' + quoteIdent(engine, name);
  }

  // Every call is scoped to one connection: its engine picks the capability
  // namespace, its id the host/Rust pool. No global engine state.
  function dbCall(conn, method, params) {
    var p = {};
    if (params) { for (var k in params) p[k] = params[k]; }
    p.connId = conn.saved.id;
    return sdk.call('db.' + conn.saved.engine + '.' + method, p);
  }

  function newConnId() {
    return 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function displayName(saved) {
    if (saved.name) return saved.name;
    if (saved.engine === 'sqlite') {
      var parts = String(saved.path).split(/[\\\\/]/);
      return parts[parts.length - 1] || saved.path || 'SQLite';
    }
    return (saved.user || '') + '@' + (saved.host || '') + ':' + (saved.port || '');
  }

  // secureStorage key for a legacy connection's password (hidden Vault items
  // since 26.10.2026, the OS keychain before). The connection migrated from
  // the legacy single-connection format keeps id 'default' and the legacy
  // '<engine>-password' key so an already-remembered password survives the
  // storage migration; everything newer gets a per-connection key. Mirrored
  // host-side by dbPasswordKeychainKey in capabilities/db.js.
  function secureKeyFor(saved) {
    return saved.id === 'default' ? saved.engine + '-password' : 'conn-' + saved.id + '-password';
  }

  function runtimeFor(saved) {
    return {
      saved: saved,
      password: '',
      createIfMissing: false,
      connected: false,
      connecting: false,
      error: false,
      status: '',
      treeNode: null,
    };
  }

  // Saved connections live in the VAULT, one item each — encrypted,
  // shareable with a team, and the same place SFTP connections live. The
  // host owns that write (db.saveConnection is id-gated to this extension);
  // the guest never touches the vault directly.
  //
  // Temporary connections are runtime-only: they show in the tree for the
  // session and are gone on restart. Keeping them in the same list as the
  // rest means the tree, the runtime map and every action treat them
  // identically — only persistence differs, and it differs here.
  function saveConnections(onlyId) {
    // Only the connection that changed, when the caller knows which.
    // Rewriting all of them on every save was N vault writes for one edit —
    // and while each of those writes now merges rather than replaces, a write
    // that cannot change anything is still a write nobody asked for.
    var persistable = connections.filter(function (c) {
      if (c.temporary) return false;
      return onlyId ? c.id === onlyId : true;
    });
    return Promise.all(
      persistable.map(function (c) {
        // The password lives on the RUNTIME connection, not on the saved
        // descriptor — so persisting the descriptor alone stored everything
        // except the one thing "save my password" was about, and every
        // reconnect asked again. (No backticks in this file: the whole guest
        // is a template literal, so one would end it mid-source.)
        var runtime = conns[c.id];
        var payload = c;
        // An explicit decision from the connection form wins over anything
        // inferable from "remember" (see passwordAction where it is set).
        var action = (runtime && runtime.passwordAction) || null;
        if (action === 'forget') {
          // Say so out loud. The host merges a partial descriptor against the
          // stored item, so merely omitting the password would PRESERVE it —
          // and "Ask for the password each time" would silently do nothing.
          payload = Object.assign({}, c, { forgetPassword: true });
        } else if (action === 'keep') {
          // Say it out loud rather than relying on the host's merge. That merge
          // only runs for a PARTIAL descriptor, and an edit-form descriptor is
          // deliberately no longer partial (openEditConnection hydrates it, so
          // an empty box can mean "cleared"). Staying silent here dropped the
          // stored password on the commonest edit of all.
          payload = Object.assign({}, c, { keepPassword: true });
        } else if (c.remember && runtime && runtime.password != null) {
          payload = Object.assign({}, c, { password: runtime.password });
        }
        if (runtime) runtime.passwordAction = null;
        return sdk.call('db.saveConnection', { connection: payload }).then(function (id) {
          // The vault mints its own id on create, so adopt it — otherwise the
          // next save would create a second item instead of updating this one.
          if (id && c.id !== id) {
            var runtime = conns[c.id];
            if (runtime) {
              delete conns[c.id];
              conns[id] = runtime;
            }
            c.id = id;
            c.origin = 'vault';
          }
        });
      })
    ).catch(function () {
      // A locked vault must not lose what was just typed. The connection stays
      // in the session either way; it simply is not persisted yet.
    });
  }

  function connFromNodeId(nodeId) {
    return conns[String(nodeId).split(SEP)[0]] || null;
  }

  // This frame is invisible (ExtensionHostLayer): conn.status alone reaches
  // nobody. Anything the user must learn — a failure, a result — also goes out
  // as an app notification (host ui.notify, text only).
  function notifyStatus(conn, tone) {
    if (!conn || !conn.status || !sdk.ui.notify) return;
    sdk.ui.notify({ tone: tone, message: conn.status }).catch(function () {});
  }

  function nid(conn, suffix) {
    return conn.saved.id + SEP + suffix;
  }

  function findNode(node, id) {
    if (node.id === id) return node;
    var kids = node.children === 'lazy' ? node.resolvedChildren || [] : Array.isArray(node.children) ? node.children : [];
    for (var i = 0; i < kids.length; i++) {
      var found = findNode(kids[i], id);
      if (found) return found;
    }
    return null;
  }

  function publishTree() {
    var roots = [];
    // A locked vault is not an empty one. Publishing nothing would hand the
    // sidebar its generic "No connections yet — add a connection" state, which
    // tells someone their saved connections do not exist when they are simply
    // behind a lock. One row that says so, with Unlock on it — no popup, the
    // way the SFTP and Terminal panels do it.
    //
    // The row is FIRST, above any surviving connections, not instead of them:
    // a lock keeps live sessions (their credentials sit on the runtime
    // object, not in the vault), so after an auto-lock the tree can hold both
    // open connections and this reminder — same layout the FTP sidebar uses.
    if (vaultLocked) {
      roots.push({
        id: '__vault_locked__',
        label: 'Vault locked',
        sublabel: 'Unlock to see your saved connections',
        icon: 'lock',
        kind: 'leaf',
        actions: [{ id: 'unlock-vault', icon: 'key', tooltip: 'Unlock vault' }],
      });
    }
    for (var i = 0; i < connections.length; i++) {
      var conn = conns[connections[i].id];
      if (conn && conn.treeNode) roots.push(conn.treeNode);
    }
    sdk.tree.publish(SECTION_ID, roots);
  }

  // Take a descriptor list as the current set of connections and redraw.
  //
  // Connections already on screen keep their RUNTIME object — a reload must not
  // drop a live connection just because the list was refetched.
  function adoptConnections(list) {
    connections = (list || []).filter(function (c) {
      return c && typeof c === 'object' && c.id && ENGINES[c.engine];
    });
    connections.forEach(function (saved) {
      if (conns[saved.id]) {
        conns[saved.id].saved = saved;
      } else {
        conns[saved.id] = runtimeFor(saved);
      }
      rebuildRoot(conns[saved.id]);
    });
    if (connections.length) lastEngine = connections[connections.length - 1].engine;
    publishTree();
    render();
  }

  // Connections that exist only at runtime — temporary dials, which nothing
  // persists — plus whatever the caller was handed. Without this a reload
  // would publish a tree with no row for a session the user is still inside.
  function withLiveTemporaries(list) {
    var incoming = {};
    list.forEach(function (c) { if (c && c.id) incoming[c.id] = true; });
    var survivors = connections.filter(function (c) {
      if (!c || !c.temporary || incoming[c.id]) return false;
      var r = conns[c.id];
      return !!(r && (r.connected || r.connecting));
    });
    return survivors.concat(list);
  }

  // Reload the connection list and redraw. Used on activation and whenever
  // the host says the vault changed — in EITHER direction: after an unlock
  // (the tree stops showing only the locked row) and after a lock (the locked
  // row appears above whatever is still open).
  // Distinguishes "the call failed" from "the answer was empty". A null used
  // to mean both, and conflating them is what froze the locked row on screen.
  // (No backticks anywhere in this file — it is a template literal.)
  var RELOAD_FAILED = {};

  function reloadConnections() {
    return sdk.call('db.listSavedConnections')
      .then(function (res) {
        vaultLocked = !!(res && res.vaultLocked);
        if (vaultLocked) {
          // The lock gates the LIST, not the sessions: a live connection
          // holds its credentials on the runtime object and keeps working
          // (same D2 rule the FTP sidebar follows). Adopting the host's
          // empty locked answer wholesale wiped those rows from the tree,
          // leaving an open connection with no row to reach it by.
          return connections.filter(function (c) {
            var r = conns[c.id];
            return !!(r && (r.connected || r.connecting));
          });
        }
        var list = (res && res.connections) || [];
        if (!list.length) return sdk.storage.get('connections');
        return list;
      })
      .catch(function () { return RELOAD_FAILED; })
      .then(function (list) {
        // A FAILED reload keeps whatever is on screen — the tree is better
        // stale than emptied because one call did not answer.
        if (list === RELOAD_FAILED) return;
        // An EMPTY answer is still an answer, and it must redraw. This used to
        // return early on a falsy list, which is exactly what an unlocked
        // vault with no saved connections produces: res.connections is empty,
        // the storage fallback resolves undefined, and the redraw was skipped
        // — leaving the previously published tree, "Vault locked" row and all,
        // frozen on screen after a successful unlock. vaultLocked was already
        // false by then; nothing ever republished to say so.
        //
        // Live TEMPORARY connections are carried across, the same D2 rule the
        // locked branch above follows. A temporary connection is persisted
        // nowhere (saveConnections skips it), so it can never come back in an
        // answer — and redrawing from a bare saved list would delete the row
        // for a session that is still open and still usable.
        adoptConnections(withLiveTemporaries(list || []));
      });
  }

`;
